// src/components/dashboard/client/NewClientCalendar.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { db } from '../../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Download, 
  Filter,
  CheckCircle2,
  AlertCircle,
  Pause,
  Play,
  RefreshCw
} from 'lucide-react';

// Types d'événements avec icônes
const EVENT_TYPES = {
  'visite-technique': { label: 'Visite technique préliminaire', icon: '🔍', color: 'text-blue-600' },
  'installation': { label: 'Installation des panneaux', icon: '🔧', color: 'text-green-600' },
  'mise-en-service': { label: 'Mise en service', icon: '⚡', color: 'text-yellow-600' },
  'maintenance': { label: 'Maintenance', icon: '🛠️', color: 'text-orange-600' },
  'rdv-client': { label: 'Rendez-vous client', icon: '🤝', color: 'text-purple-600' },
  'controle': { label: 'Contrôle qualité', icon: '✅', color: 'text-emerald-600' },
  'autre': { label: 'Autre', icon: '📋', color: 'text-gray-600' }
};

// Statuts avec styles
const EVENT_STATUS = {
  'a-programmer': { 
    label: 'À programmer', 
    icon: <Clock className="w-4 h-4" />, 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    darkColor: 'bg-yellow-900/20 text-yellow-300 border-yellow-700'
  },
  'planifie': { 
    label: 'Planifié', 
    icon: <Calendar className="w-4 h-4" />, 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    darkColor: 'bg-blue-900/20 text-blue-300 border-blue-700'
  },
  'en-cours': { 
    label: 'En cours', 
    icon: <Play className="w-4 h-4" />, 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    darkColor: 'bg-orange-900/20 text-orange-300 border-orange-700'
  },
  'termine': { 
    label: 'Terminé', 
    icon: <CheckCircle2 className="w-4 h-4" />, 
    color: 'bg-green-100 text-green-800 border-green-200',
    darkColor: 'bg-green-900/20 text-green-300 border-green-700'
  },
  'reporte': { 
    label: 'Reporté', 
    icon: <Pause className="w-4 h-4" />, 
    color: 'bg-red-100 text-red-800 border-red-200',
    darkColor: 'bg-red-900/20 text-red-300 border-red-700'
  }
};

const NewClientCalendar = () => {
  const { dossierId } = useParams();
  const themeContext = useTheme();
  const theme = themeContext?.theme || 'light';
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed
  const [admins, setAdmins] = useState({});

  console.log("🗓️ NewClientCalendar chargé pour dossier:", dossierId);

  // Chargement des événements et des admins
  useEffect(() => {
    const loadData = async () => {
      if (!dossierId) return;
      
      setLoading(true);
      try {
        // Charger les admins d'abord
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const adminsData = {};
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          adminsData[doc.id] = {
            name: userData.displayName || userData.email,
            role: userData.role,
            email: userData.email
          };
        });
        setAdmins(adminsData);

        // Charger les événements pour ce dossier
        const eventsQuery = query(
          collection(db, 'events'),
          where('dossierId', '==', dossierId)
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
            end: data.end?.toDate ? data.end.toDate() : new Date(data.end)
          };
        });

        // Trier par date
        eventsData.sort((a, b) => new Date(a.start) - new Date(b.start));
        
        console.log("📅 Événements trouvés:", eventsData.length);
        setEvents(eventsData);
      } catch (error) {
        console.error('Erreur lors du chargement des événements:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dossierId]);

  // Filtrage des événements
  const filteredEvents = events.filter(event => {
    const now = new Date();
    const eventDate = new Date(event.start);
    
    switch (filter) {
      case 'upcoming':
        return eventDate >= now && event.status !== 'termine';
      case 'completed':
        return event.status === 'termine';
      default:
        return true;
    }
  });

  // Génération du fichier .ics pour export Google Calendar
  const generateICSFile = (eventsList = filteredEvents) => {
    const formatDate = (date) => {
      return date.toISOString().replace(/[:\-]|\.\d{3}/g, '');
    };

    const icsEvents = eventsList.map(event => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60 * 1000);
      
      return [
        'BEGIN:VEVENT',
        `UID:${event.id}@energesync.com`,
        `DTSTAMP:${formatDate(new Date())}`,
        `DTSTART:${formatDate(startDate)}`,
        `DTEND:${formatDate(endDate)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description || ''}\\n\\nStatut: ${EVENT_STATUS[event.status]?.label || event.status}`,
        event.location ? `LOCATION:${event.location}` : '',
        'END:VEVENT'
      ].filter(line => line !== '').join('\r\n');
    }).join('\r\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EnergeSync//Calendar Client//FR',
      'CALNAME:Planning Interventions - EnergeSync',
      icsEvents,
      'END:VCALENDAR'
    ].join('\r\n');

    return icsContent;
  };

  // Export vers Google Calendar
  const handleExportToGoogle = () => {
    const icsContent = generateICSFile();
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planning_interventions_${dossierId}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Formatage des dates
  const formatEventDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatEventTime = (date) => {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-white' : 'text-gray-600'}>
            Chargement de votre planning...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className={`rounded-2xl p-8 mb-8 shadow-xl ${
          theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* NOUVEAU: Bouton retour au dashboard */}
              <button 
                onClick={() => window.history.back()}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${
                  theme === 'dark' 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                } shadow-md hover:shadow-lg`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Retour</span>
              </button>
              
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>
              
              <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  📅 Planning des interventions
                </h1>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Suivez vos rendez-vous et interventions
                </p>
              </div>
            </div>
            
            {/* Bouton Export */}
            <button
              onClick={handleExportToGoogle}
              disabled={events.length === 0}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } shadow-lg hover:shadow-xl`}
            >
              <Download className="w-5 h-5" />
              <span>Exporter</span>
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className={`rounded-xl p-6 mb-6 shadow-lg ${
          theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
        }`}>
          <div className="flex items-center space-x-4">
            <Filter className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
            <div className="flex space-x-2">
              {[
                { key: 'all', label: 'Tous', count: events.length },
                { key: 'upcoming', label: 'À venir', count: events.filter(e => new Date(e.start) >= new Date() && e.status !== 'termine').length },
                { key: 'completed', label: 'Terminés', count: events.filter(e => e.status === 'termine').length }
              ].map(filterOption => (
                <button
                  key={filterOption.key}
                  onClick={() => setFilter(filterOption.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    filter === filterOption.key
                      ? theme === 'dark'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-blue-500 text-white shadow-lg'
                      : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterOption.label} ({filterOption.count})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Liste des événements */}
        {filteredEvents.length === 0 ? (
          <div className={`rounded-xl p-12 text-center shadow-lg ${
            theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
          }`}>
            <Calendar className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
            <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Aucune intervention programmée
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {filter === 'all' 
                ? "Aucun événement n'est encore planifié pour votre dossier."
                : filter === 'upcoming'
                  ? "Aucune intervention à venir."
                  : "Aucune intervention terminée."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event, index) => {
              const eventType = EVENT_TYPES[event.eventType] || EVENT_TYPES['autre'];
              const statusInfo = EVENT_STATUS[event.status] || EVENT_STATUS['a-programmer'];
              const responsible = admins[event.responsableId];
              
              return (
                <div
                  key={event.id}
                  className={`rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                    theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
                  }`}
                >
                  {/* En-tête de l'événement */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="text-3xl">{eventType.icon}</div>
                      <div>
                        <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {event.title}
                        </h3>
                        <p className={`text-sm ${eventType.color} font-medium`}>
                          {eventType.label}
                        </p>
                      </div>
                    </div>
                    
                    {/* Statut */}
                    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${
                      theme === 'dark' ? statusInfo.darkColor : statusInfo.color
                    }`}>
                      {statusInfo.icon}
                      <span className="text-sm font-medium">{statusInfo.label}</span>
                    </div>
                  </div>

                  {/* Description */}
                  {event.description && (
                    <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {event.description}
                    </p>
                  )}

                  {/* Détails */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Date et heure */}
                    <div className="flex items-center space-x-3">
                      <Calendar className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <div>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {formatEventDate(event.start)}
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formatEventTime(event.start)}
                          {event.end && ` - ${formatEventTime(event.end)}`}
                        </p>
                      </div>
                    </div>

                    {/* Responsable */}
                    {responsible && (
                      <div className="flex items-center space-x-3">
                        <User className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                        <div>
                          <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {responsible.name}
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {responsible.role}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lieu */}
                  {event.location && (
                    <div className="flex items-center space-x-3">
                      <MapPin className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {event.location}
                      </p>
                    </div>
                  )}

                  {/* Couleur de l'événement */}
                  {event.color && (
                    <div className="flex items-center justify-end mt-4">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Assigné à :
                        </span>
                        <div 
                          className="w-4 h-4 rounded-full border-2 border-white shadow"
                          style={{ backgroundColor: event.color }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer avec info export */}
        <div className={`mt-8 p-4 rounded-lg ${
          theme === 'dark' ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center space-x-2">
            <Download className="w-5 h-5 text-blue-600" />
            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
              💡 Exportez vos interventions vers Google Calendar, Outlook ou Apple Calendar !
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewClientCalendar;